import { useState } from 'react';
import { useTheme, type ThemeConfig } from '@/context/ThemeContext';

const SIDEBAR_PRESETS = [
  { label: 'Branco', bg: '#ffffff', accent: '#16a34a', style: 'light' as const },
  { label: 'Cinza', bg: '#f4f4f5', accent: '#16a34a', style: 'light' as const },
  { label: 'Verde Escuro', bg: '#14532d', accent: '#4ade80', style: 'dark' as const },
  { label: 'Verde Suave', bg: '#064e3b', accent: '#86efac', style: 'dark' as const },
  { label: 'Noite', bg: '#0f172a', accent: '#22c55e', style: 'dark' as const },
  { label: 'Carvão', bg: '#18181b', accent: '#4ade80', style: 'dark' as const },
];

const PRIMARY_PRESETS = [
  { label: 'Verde', value: '#16a34a' },
  { label: 'Verde Escuro', value: '#15803d' },
  { label: 'Esmeralda', value: '#065f46' },
  { label: 'Teal', value: '#0d9488' },
  { label: 'Azul Petróleo', value: '#0f4c75' },
  { label: 'Ardósia', value: '#0f172a' },
];

const FONT_OPTIONS = [
  { label: 'Inter', value: 'Inter', preview: 'Aa' },
  { label: 'DM Sans', value: 'DM Sans', preview: 'Aa' },
  { label: 'Nunito', value: 'Nunito', preview: 'Aa' },
  { label: 'Outfit', value: 'Outfit', preview: 'Aa' },
  { label: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans', preview: 'Aa' },
];

const FONT_SIZE_OPTIONS: { label: string; value: ThemeConfig['fontSize'] }[] = [
  { label: 'Pequena', value: 'sm' },
  { label: 'Normal', value: 'md' },
  { label: 'Grande', value: 'lg' },
];

const FONT_WEIGHT_OPTIONS: { label: string; value: ThemeConfig['fontWeight'] }[] = [
  { label: 'Leve', value: 'light' },
  { label: 'Normal', value: 'normal' },
  { label: 'Médio', value: 'medium' },
];

const RADIUS_OPTIONS: { label: string; value: ThemeConfig['borderRadius']; preview: string }[] = [
  { label: 'Reto', value: 'sharp', preview: 'rounded-sm' },
  { label: 'Suave', value: 'rounded', preview: 'rounded-lg' },
  { label: 'Pill', value: 'pill', preview: 'rounded-full' },
];

// Galeria de ícones para páginas do módulo de Formação
const ICON_GALLERY: Record<string, { icon: string; label: string }[]> = {
  '/formacao': [
    { icon: 'ri-dashboard-line', label: 'Dashboard' },
    { icon: 'ri-pie-chart-line', label: 'Pizza' },
    { icon: 'ri-bar-chart-line', label: 'Barras' },
    { icon: 'ri-book-open-line', label: 'Livro' },
    { icon: 'ri-clipboard-line', label: 'Clipboard' },
    { icon: 'ri-survey-line', label: 'Resumo' },
  ],
  '/formacao/acoes': [
    { icon: 'ri-book-open-line', label: 'Livro' },
    { icon: 'ri-graduation-cap-line', label: 'Capelo' },
    { icon: 'ri-presentation-line', label: 'Pres.' },
    { icon: 'ri-slideshow-line', label: 'Slides' },
    { icon: 'ri-lightbulb-line', label: 'Ideia' },
    { icon: 'ri-stack-line', label: 'Stack' },
  ],
  '/formacao/funcionarios': [
    { icon: 'ri-team-line', label: 'Equipa' },
    { icon: 'ri-user-line', label: 'User' },
    { icon: 'ri-group-line', label: 'Grupo' },
    { icon: 'ri-contacts-line', label: 'Cont.' },
    { icon: 'ri-id-card-line', label: 'Cartão' },
    { icon: 'ri-profile-line', label: 'Perfil' },
  ],
  '/formacao/participantes': [
    { icon: 'ri-user-star-line', label: 'Estrela' },
    { icon: 'ri-user-follow-line', label: 'Check' },
    { icon: 'ri-user-add-line', label: 'Add' },
    { icon: 'ri-team-line', label: 'Equipa' },
    { icon: 'ri-emotion-line', label: 'Smile' },
    { icon: 'ri-award-line', label: 'Prémio' },
  ],
  '/formacao/plano': [
    { icon: 'ri-clipboard-line', label: 'Clip.' },
    { icon: 'ri-calendar-check-line', label: 'Cal.' },
    { icon: 'ri-planet-line', label: 'Planeta' },
    { icon: 'ri-map-line', label: 'Mapa' },
    { icon: 'ri-list-check', label: 'Lista' },
    { icon: 'ri-todo-line', label: 'Todo' },
  ],
  '/formacao/documentos': [
    { icon: 'ri-folder-open-line', label: 'Pasta' },
    { icon: 'ri-file-list-3-line', label: 'Lista' },
    { icon: 'ri-archive-line', label: 'Arq.' },
    { icon: 'ri-attachment-line', label: 'Clip' },
    { icon: 'ri-file-cloud-line', label: 'Nuvem' },
    { icon: 'ri-book-3-line', label: 'Livro' },
  ],
  '/formacao/relatorios': [
    { icon: 'ri-pie-chart-2-line', label: 'Pizza' },
    { icon: 'ri-file-chart-2-line', label: 'Gráfico' },
    { icon: 'ri-bar-chart-grouped-line', label: 'Barras' },
    { icon: 'ri-newspaper-line', label: 'Jornal' },
    { icon: 'ri-file-text-line', label: 'Texto' },
    { icon: 'ri-printer-line', label: 'Print' },
  ],
  '/formacao/calendario': [
    { icon: 'ri-calendar-event-line', label: 'Cal.' },
    { icon: 'ri-calendar-todo-line', label: 'Todo' },
    { icon: 'ri-calendar-check-line', label: 'Check' },
    { icon: 'ri-time-line', label: 'Tempo' },
    { icon: 'ri-history-line', label: 'Agenda' },
    { icon: 'ri-bookmark-line', label: 'Marca' },
  ],
  '/formacao/entidades': [
    { icon: 'ri-building-2-line', label: 'Prédio' },
    { icon: 'ri-building-line', label: 'Bld.' },
    { icon: 'ri-bank-line', label: 'Banco' },
    { icon: 'ri-community-line', label: 'Com.' },
    { icon: 'ri-school-line', label: 'Escola' },
    { icon: 'ri-organization-chart', label: 'Org.' },
  ],
  '/formacao/alertas': [
    { icon: 'ri-notification-3-line', label: 'Sino' },
    { icon: 'ri-alarm-warning-line', label: 'Alarme' },
    { icon: 'ri-error-warning-line', label: 'Aviso' },
    { icon: 'ri-bell-line', label: 'Bell' },
    { icon: 'ri-flashlight-line', label: 'Flash' },
    { icon: 'ri-megaphone-line', label: 'Mega' },
  ],
  '/formacao/requisitos': [
    { icon: 'ri-scales-3-line', label: 'Balança' },
    { icon: 'ri-shield-check-line', label: 'Shield' },
    { icon: 'ri-file-shield-2-line', label: 'Fich.' },
    { icon: 'ri-verified-badge-line', label: 'Certif.' },
    { icon: 'ri-auction-line', label: 'Martelo' },
    { icon: 'ri-bookmark-3-line', label: 'Marca' },
  ],
  '/formacao/avaliacoes': [
    { icon: 'ri-star-line', label: 'Estrela' },
    { icon: 'ri-medal-line', label: 'Medalha' },
    { icon: 'ri-award-line', label: 'Prémio' },
    { icon: 'ri-emotion-line', label: 'Smile' },
    { icon: 'ri-thumb-up-line', label: 'Like' },
    { icon: 'ri-trophy-line', label: 'Rank' },
  ],
  '/formacao/formadores': [
    { icon: 'ri-user-voice-line', label: 'Voz' },
    { icon: 'ri-user-star-line', label: 'Estrela' },
    { icon: 'ri-user-settings-line', label: 'Config.' },
    { icon: 'ri-user-line', label: 'User' },
    { icon: 'ri-mic-line', label: 'Mic' },
    { icon: 'ri-presentation-line', label: 'Pres.' },
  ],
  '/formacao/notificacoes': [
    { icon: 'ri-mail-send-line', label: 'Enviar' },
    { icon: 'ri-mail-settings-line', label: 'Config.' },
    { icon: 'ri-mail-check-line', label: 'Check' },
    { icon: 'ri-mail-line', label: 'Mail' },
    { icon: 'ri-mail-open-line', label: 'Aberto' },
    { icon: 'ri-send-plane-line', label: 'Avião' },
  ],
  '/formacao/indicadores': [
    { icon: 'ri-bar-chart-box-line', label: 'Barras' },
    { icon: 'ri-line-chart-line', label: 'Linhas' },
    { icon: 'ri-pie-chart-2-line', label: 'Pizza' },
    { icon: 'ri-bar-chart-horizontal-line', label: 'Horiz.' },
    { icon: 'ri-funds-box-line', label: 'Fundos' },
    { icon: 'ri-trophy-line', label: 'Rank' },
  ],
};

// Helper: get formacao- prefixed icon from theme
function getFormacaoNavIcon(theme: ThemeConfig, path: string): string {
  return (theme.formacaoNavIcons ?? {})[path] || DEFAULT_NAV_ICONS[path] || 'ri-circle-line';
}

const DEFAULT_NAV_ICONS: Record<string, string> = {
  '/formacao': 'ri-dashboard-line',
  '/formacao/acoes': 'ri-book-open-line',
  '/formacao/funcionarios': 'ri-team-line',
  '/formacao/participantes': 'ri-user-star-line',
  '/formacao/plano': 'ri-clipboard-line',
  '/formacao/documentos': 'ri-folder-open-line',
  '/formacao/relatorios': 'ri-pie-chart-2-line',
  '/formacao/calendario': 'ri-calendar-event-line',
  '/formacao/entidades': 'ri-building-2-line',
  '/formacao/alertas': 'ri-notification-3-line',
  '/formacao/requisitos': 'ri-scales-3-line',
  '/formacao/avaliacoes': 'ri-star-line',
  '/formacao/formadores': 'ri-user-voice-line',
  '/formacao/notificacoes': 'ri-mail-send-line',
  '/formacao/indicadores': 'ri-bar-chart-box-line',
};

export default function AppearanceFormacaoTab() {
  const { theme, setTheme, saveTheme, saving } = useTheme();
  const [saved, setSaved] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>(theme.formacaoLogoUrl || '');
  const [heroPreview, setHeroPreview] = useState<string>(theme.formacaoHeroImageUrl || '');
  const [faviconPreview, setFaviconPreview] = useState<string>(theme.faviconUrl || '');
  const [openGallery, setOpenGallery] = useState<string | null>(null);

  const handleSave = async () => {
    await saveTheme();
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'formacaoLogoUrl' | 'formacaoHeroImageUrl' | 'faviconUrl',
    setPreview: (v: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setPreview(result);
      setTheme({ [field]: result });
    };
    reader.readAsDataURL(file);
  };

  const effectiveBg = theme.formacaoSidebarBg ?? '#ffffff';
  const effectiveAccent = theme.formacaoAccentColor ?? '#16a34a';
  const effectiveStyle = theme.formacaoSidebarStyle ?? 'light';
  const isLight = effectiveStyle === 'light';

  return (
    <div className="space-y-6">

      {/* ── Identidade ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
            <i className="ri-book-open-line text-emerald-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Identidade da Gestão da Formação</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Nome e subtítulo que aparecem no topo da sidebar do módulo de Formação</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                Nome do Gabinete
              </label>
              <input
                type="text"
                value={theme.formacaoNome ?? 'Gabinete de Formação'}
                onChange={(e) => setTheme({ formacaoNome: e.target.value })}
                placeholder="Gabinete de Formação"
                className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 transition-colors"
              />
              <p className="text-[11px] text-zinc-400 mt-1.5">Aparece em destaque no topo da sidebar do módulo de Formação</p>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                Subtítulo da Sidebar
              </label>
              <input
                type="text"
                value={theme.formacaoSubtitulo ?? 'Gestão da Formação'}
                onChange={(e) => setTheme({ formacaoSubtitulo: e.target.value })}
                placeholder="Gestão da Formação"
                className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 transition-colors"
              />
              <p className="text-[11px] text-zinc-400 mt-1.5">Linha secundária abaixo do nome</p>
            </div>
          </div>

          {/* Preview sidebar header */}
          <div className="border-t border-zinc-100 pt-5">
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Pré-visualização do Cabeçalho</p>
            <div
              className="rounded-xl border border-zinc-200 p-4 flex items-center gap-3"
              style={{ backgroundColor: effectiveBg }}
            >
              <div
                className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center"
                style={{ backgroundColor: `${effectiveAccent}15`, border: `1px solid ${effectiveAccent}30` }}
              >
                <i className="ri-book-open-line text-sm" style={{ color: effectiveAccent }} />
              </div>
              <div>
                <p className="text-[13px] font-bold" style={{ color: isLight ? '#18181b' : '#ffffff' }}>
                  {theme.formacaoNome || 'Gabinete de Formação'}
                </p>
                <p className="text-[11px] font-semibold" style={{ color: effectiveAccent }}>
                  {theme.formacaoSubtitulo || 'Gestão da Formação'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Navegação — Negrito ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
            <i className="ri-menu-line text-emerald-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Itens de Navegação — Formação</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Escolha quais os itens do menu do módulo de Formação que aparecem em negrito</p>
          </div>
        </div>
        <div className="p-6">
          <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Negrito nos itens do menu</p>
          <div className="space-y-2">
            {[
              { to: '/formacao', label: 'Resumo Executivo', icon: 'ri-dashboard-line' },
              { to: '/formacao/funcionarios', label: 'Funcionários', icon: 'ri-team-line' },
              { to: '/formacao/acoes', label: 'Ações de Formação', icon: 'ri-book-open-line' },
              { to: '/formacao/participantes', label: 'Participantes', icon: 'ri-user-star-line' },
              { to: '/formacao/plano', label: 'Plano de Formação', icon: 'ri-clipboard-line' },
              { to: '/formacao/documentos', label: 'Documentos', icon: 'ri-folder-open-line' },
              { to: '/formacao/relatorios', label: 'Relatórios', icon: 'ri-pie-chart-2-line' },
              { to: '/formacao/calendario', label: 'Calendário', icon: 'ri-calendar-event-line' },
              { to: '/formacao/entidades', label: 'Entidades Formadoras', icon: 'ri-building-2-line' },
              { to: '/formacao/alertas', label: 'Alertas de Formação', icon: 'ri-notification-3-line' },
              { to: '/formacao/requisitos', label: 'Requisitos Legais', icon: 'ri-scales-3-line' },
              { to: '/formacao/avaliacoes', label: 'Avaliações', icon: 'ri-star-line' },
              { to: '/formacao/formadores', label: 'Formadores Internos', icon: 'ri-user-voice-line' },
              { to: '/formacao/notificacoes', label: 'Notificações Email', icon: 'ri-mail-send-line' },
              { to: '/formacao/indicadores', label: 'Indicadores Qualidade', icon: 'ri-bar-chart-box-line' },
            ].map((item) => {
              const boldItems = theme.formacaoNavBoldItems ?? ['/formacao'];
              const isBold = boldItems.includes(item.to);
              return (
                <label
                  key={item.to}
                  className="flex items-center justify-between px-4 py-3 rounded-xl border border-zinc-100 hover:border-zinc-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-zinc-500 text-sm`} />
                    </div>
                    <span
                      className="text-[13px] text-zinc-700"
                      style={{ fontWeight: isBold ? 700 : 400 }}
                    >
                      {item.label}
                    </span>
                    {isBold && (
                      <span className="text-[10px] font-semibold text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded-full">Bold</span>
                    )}
                  </div>
                  <div
                    onClick={() => {
                      const current = theme.formacaoNavBoldItems ?? ['/formacao'];
                      const next = isBold
                        ? current.filter((x) => x !== item.to)
                        : [...current, item.to];
                      setTheme({ formacaoNavBoldItems: next });
                    }}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${isBold ? 'bg-emerald-600' : 'bg-zinc-200'}`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isBold ? 'translate-x-5' : 'translate-x-0.5'}`}
                    />
                  </div>
                </label>
              );
            })}
          </div>
          <p className="text-[11px] text-zinc-400 mt-3">
            Os itens em negrito ficam com <strong>font-weight: 700</strong> no menu de Formação.
          </p>
        </div>
      </div>

      {/* ── Ícones da Sidebar — Formação ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
            <i className="ri-apps-line text-emerald-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Ícones da Sidebar — Formação</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Personalize o ícone de cada item do menu do módulo de Formação</p>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {[
              { to: '/formacao', label: 'Resumo', defaultIcon: 'ri-dashboard-line' },
              { to: '/formacao/acoes', label: 'Ações', defaultIcon: 'ri-book-open-line' },
              { to: '/formacao/funcionarios', label: 'Funcionários', defaultIcon: 'ri-team-line' },
              { to: '/formacao/participantes', label: 'Participantes', defaultIcon: 'ri-user-star-line' },
              { to: '/formacao/plano', label: 'Plano', defaultIcon: 'ri-clipboard-line' },
              { to: '/formacao/documentos', label: 'Documentos', defaultIcon: 'ri-folder-open-line' },
              { to: '/formacao/relatorios', label: 'Relatórios', defaultIcon: 'ri-pie-chart-2-line' },
              { to: '/formacao/calendario', label: 'Calendário', defaultIcon: 'ri-calendar-event-line' },
              { to: '/formacao/entidades', label: 'Entidades Formadoras', defaultIcon: 'ri-building-2-line' },
              { to: '/formacao/alertas', label: 'Alertas de Formação', defaultIcon: 'ri-notification-3-line' },
              { to: '/formacao/requisitos', label: 'Requisitos Legais', defaultIcon: 'ri-scales-3-line' },
              { to: '/formacao/avaliacoes', label: 'Avaliações', defaultIcon: 'ri-star-line' },
              { to: '/formacao/formadores', label: 'Formadores Internos', defaultIcon: 'ri-user-voice-line' },
              { to: '/formacao/notificacoes', label: 'Notificações Email', defaultIcon: 'ri-mail-send-line' },
              { to: '/formacao/indicadores', label: 'Indicadores Qualidade', defaultIcon: 'ri-bar-chart-box-line' },
            ].map((item) => {
              const currentIcon = getFormacaoNavIcon(theme, item.to);
              const isGalleryOpen = openGallery === item.to;
              const suggestions = ICON_GALLERY[item.to] ?? [];
              return (
                <div key={item.to} className="rounded-xl border border-zinc-100 overflow-hidden">
                  {/* Row principal */}
                  <div className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50/50 transition-colors">
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ backgroundColor: effectiveBg === '#ffffff' ? '#f4f4f5' : effectiveBg }}
                    >
                      <i className={`${currentIcon} text-base`} style={{ color: effectiveAccent }} />
                    </div>
                    <span className="text-[13px] font-medium text-zinc-700 w-28 flex-shrink-0">{item.label}</span>
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={currentIcon}
                        onChange={(e) => {
                          const next = { ...(theme.formacaoNavIcons ?? {}), [item.to]: e.target.value };
                          setTheme({ formacaoNavIcons: next });
                        }}
                        placeholder={item.defaultIcon}
                        className="w-full px-3 py-2 text-[12px] font-mono border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 transition-colors"
                      />
                    </div>
                    <button
                      onClick={() => setOpenGallery(isGalleryOpen ? null : item.to)}
                      className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold rounded-lg border transition-colors cursor-pointer flex-shrink-0 ${
                        isGalleryOpen
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'text-zinc-500 border-zinc-200 hover:border-zinc-400 hover:text-zinc-700'
                      }`}
                    >
                      <i className="ri-grid-line text-xs" />
                      Galeria
                    </button>
                    <button
                      onClick={() => {
                        const next = { ...(theme.formacaoNavIcons ?? {}) };
                        delete next[item.to];
                        setTheme({ formacaoNavIcons: next });
                      }}
                      className="whitespace-nowrap w-8 h-8 flex items-center justify-center text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                      title="Repor ícone padrão"
                    >
                      <i className="ri-refresh-line text-sm" />
                    </button>
                  </div>

                  {/* Galeria de sugestões */}
                  {isGalleryOpen && (
                    <div className="border-t border-zinc-100 px-4 py-4 bg-zinc-50/60">
                      <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">
                        Ícones sugeridos para "{item.label}"
                      </p>
                      <div className="grid grid-cols-6 gap-2">
                        {suggestions.map((s) => {
                          const isSelected = currentIcon === s.icon;
                          return (
                            <button
                              key={s.icon}
                              onClick={() => {
                                const next = { ...(theme.formacaoNavIcons ?? {}), [item.to]: s.icon };
                                setTheme({ formacaoNavIcons: next });
                                setOpenGallery(null);
                              }}
                              title={s.label}
                              className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all cursor-pointer ${
                                isSelected
                                  ? 'border-emerald-600 bg-emerald-600'
                                  : 'border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50'
                              }`}
                            >
                              <div
                                className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                                style={{ backgroundColor: isSelected ? 'rgba(255,255,255,0.15)' : effectiveBg }}
                              >
                                <i
                                  className={`${s.icon} text-sm`}
                                  style={{ color: isSelected ? '#ffffff' : effectiveAccent }}
                                />
                              </div>
                              <span className={`text-[10px] font-medium leading-tight text-center ${isSelected ? 'text-white' : 'text-zinc-500'}`}>
                                {s.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-zinc-400 mt-3">
                        Clique num ícone para selecionar. Pode também escrever qualquer classe Remix Icon no campo de texto acima.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 px-3 py-3 bg-zinc-50 rounded-xl">
            <p className="text-[11px] text-zinc-500 font-medium mb-1">Como usar</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Clique em <strong>Galeria</strong> para escolher visualmente, ou escreva diretamente a classe Remix Icon, ex: <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-zinc-200">ri-home-line</span>.
            </p>
          </div>
        </div>
      </div>

      {/* ── Barra Lateral ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
            <i className="ri-layout-left-line text-emerald-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Barra Lateral do Módulo de Formação</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Cor de fundo, destaque e cor do texto da sidebar do módulo de Formação</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Presets</p>
            <div className="grid grid-cols-3 gap-2">
              {SIDEBAR_PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => setTheme({ formacaoSidebarBg: p.bg, formacaoAccentColor: p.accent, formacaoSidebarStyle: p.style })}
                  className={`relative flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                    theme.formacaoSidebarBg === p.bg
                      ? 'border-emerald-600 scale-[1.02]'
                      : 'border-zinc-100 hover:border-zinc-300'
                  }`}
                >
                  <div
                    className="w-full h-10 rounded-lg flex items-center justify-center gap-1.5"
                    style={{ backgroundColor: p.bg }}
                  >
                    <div className="w-1.5 h-5 rounded-full opacity-40" style={{ backgroundColor: p.accent }} />
                    <div className="w-1.5 h-3 rounded-full opacity-25" style={{ backgroundColor: p.accent }} />
                    <div className="w-1.5 h-4 rounded-full opacity-25" style={{ backgroundColor: p.accent }} />
                  </div>
                  <span className="text-[11px] font-medium text-zinc-600">{p.label}</span>
                  {theme.formacaoSidebarBg === p.bg && (
                    <div className="absolute top-1.5 right-1.5 w-4 h-4 flex items-center justify-center bg-emerald-600 rounded-full">
                      <i className="ri-check-line text-white text-[9px]" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Cor de Fundo</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={theme.formacaoSidebarBg ?? '#ffffff'}
                  onChange={(e) => setTheme({ formacaoSidebarBg: e.target.value })}
                  className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-0.5"
                />
                <input
                  type="text"
                  value={theme.formacaoSidebarBg ?? '#ffffff'}
                  onChange={(e) => setTheme({ formacaoSidebarBg: e.target.value })}
                  className="flex-1 px-3 py-2 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Cor de Destaque</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={theme.formacaoAccentColor ?? '#16a34a'}
                  onChange={(e) => setTheme({ formacaoAccentColor: e.target.value })}
                  className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-0.5"
                />
                <input
                  type="text"
                  value={theme.formacaoAccentColor ?? '#16a34a'}
                  onChange={(e) => setTheme({ formacaoAccentColor: e.target.value })}
                  className="flex-1 px-3 py-2 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Cor do Texto</label>
              <div className="flex gap-2">
                {([
                  { value: 'auto', label: 'Auto' },
                  { value: 'black', label: 'Preto' },
                  { value: 'white', label: 'Branco' },
                ] as { value: 'auto' | 'black' | 'white'; label: string }[]).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTheme({ formacaoNavTextColor: opt.value })}
                    className={`flex-1 py-2 rounded-lg border-2 text-[12px] font-medium transition-all cursor-pointer ${
                      (theme.formacaoNavTextColor ?? 'auto') === opt.value
                        ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                        : 'border-zinc-100 text-zinc-500 hover:border-zinc-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cores da Página ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
            <i className="ri-palette-line text-emerald-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Cores da Página</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Cor primária e fundo dos botões da Formação</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Cor Primária (Botões)</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {PRIMARY_PRESETS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setTheme({ primaryColor: p.value })}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-[12px] font-medium transition-all cursor-pointer ${
                    theme.primaryColor === p.value
                      ? 'border-emerald-600 text-zinc-900'
                      : 'border-zinc-100 text-zinc-500 hover:border-zinc-300'
                  }`}
                >
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: p.value }} />
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={theme.primaryColor}
                onChange={(e) => setTheme({ primaryColor: e.target.value })}
                className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-0.5"
              />
              <input
                type="text"
                value={theme.primaryColor}
                onChange={(e) => setTheme({ primaryColor: e.target.value })}
                className="w-36 px-3 py-2 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 font-mono"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Tipografia ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
            <i className="ri-text text-emerald-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Tipografia</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Tipo de letra, tamanho e peso</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Tipo de Letra</p>
            <div className="grid grid-cols-5 gap-2">
              {FONT_OPTIONS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setTheme({ fontFamily: f.value })}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                    theme.fontFamily === f.value
                      ? 'border-emerald-600 bg-emerald-600 text-white'
                      : 'border-zinc-100 hover:border-zinc-300'
                  }`}
                >
                  <span
                    className="text-2xl font-semibold text-zinc-800"
                    style={{ fontFamily: `'${f.value}', sans-serif` }}
                  >
                    {f.preview}
                  </span>
                  <span className="text-[11px] font-medium text-zinc-500 text-center leading-tight">{f.label}</span>
                  {theme.fontFamily === f.value && (
                    <div className="w-4 h-4 flex items-center justify-center bg-emerald-600 rounded-full">
                      <i className="ri-check-line text-white text-[9px]" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-5">
            <div>
              <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Tamanho</p>
              <div className="flex gap-2">
                {FONT_SIZE_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setTheme({ fontSize: o.value })}
                    className={`flex-1 py-2 rounded-lg border-2 text-[12px] font-medium transition-all cursor-pointer ${
                      theme.fontSize === o.value
                        ? 'border-emerald-600 bg-emerald-600 text-white'
                        : 'border-zinc-100 text-zinc-500 hover:border-zinc-300'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Peso</p>
              <div className="flex gap-2">
                {FONT_WEIGHT_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setTheme({ fontWeight: o.value })}
                    className={`flex-1 py-2 rounded-lg border-2 text-[12px] font-medium transition-all cursor-pointer ${
                      theme.fontWeight === o.value
                        ? 'border-emerald-600 bg-emerald-600 text-white'
                        : 'border-zinc-100 text-zinc-500 hover:border-zinc-300'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Cantos</p>
              <div className="flex gap-2">
                {RADIUS_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setTheme({ borderRadius: o.value })}
                    className={`flex-1 py-2 rounded-lg border-2 text-[12px] font-medium transition-all cursor-pointer ${
                      theme.borderRadius === o.value
                        ? 'border-emerald-600 bg-emerald-600 text-white'
                        : 'border-zinc-100 text-zinc-500 hover:border-zinc-300'
                    } ${o.preview}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Imagens ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
            <i className="ri-image-line text-emerald-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Imagens do Módulo de Formação</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Logótipo e imagem de fundo da sidebar</p>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Logo */}
            <div>
              <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Logótipo da Formação</p>
              <div className="border-2 border-dashed border-zinc-200 rounded-xl p-4 flex flex-col items-center gap-3 hover:border-zinc-400 transition-colors">
                {logoPreview ? (
                  <div className="relative w-full">
                    <img src={logoPreview} alt="Logo" className="w-full object-contain rounded-lg" style={{ height: `${theme.formacaoLogoSize ?? 26}px`, maxHeight: '80px' }} />
                    <button
                      onClick={() => { setLogoPreview(''); setTheme({ formacaoLogoUrl: '' }); }}
                      className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full cursor-pointer hover:bg-red-600 transition-colors"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center bg-zinc-100 rounded-xl">
                    <i className="ri-image-add-line text-2xl text-zinc-400" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <span className="text-[12px] font-medium text-zinc-600 hover:text-zinc-900 transition-colors">
                    {logoPreview ? 'Alterar imagem' : 'Carregar logótipo'}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'formacaoLogoUrl', setLogoPreview)} />
                </label>
                <p className="text-[11px] text-zinc-400 text-center">PNG, SVG ou JPG · Recomendado 200×60px</p>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">Tamanho do Logótipo</label>
                  <span className="text-[12px] font-bold text-zinc-700">{theme.formacaoLogoSize ?? 26}px</span>
                </div>
                <input
                  type="range" min={16} max={80} step={2}
                  value={theme.formacaoLogoSize ?? 26}
                  onChange={(e) => setTheme({ formacaoLogoSize: parseInt(e.target.value, 10) })}
                  className="w-full accent-emerald-600 cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-zinc-400 mt-0.5">
                  <span>Pequeno</span><span>Grande</span>
                </div>
              </div>
            </div>

            {/* Hero / Fundo */}
            <div>
              <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Imagem de Fundo (Sidebar)</p>
              <div className="border-2 border-dashed border-zinc-200 rounded-xl p-4 flex flex-col items-center gap-3 hover:border-zinc-400 transition-colors">
                {heroPreview ? (
                  <div className="relative w-full">
                    <img src={heroPreview} alt="Hero" className="w-full h-20 object-cover rounded-lg" />
                    <button
                      onClick={() => { setHeroPreview(''); setTheme({ formacaoHeroImageUrl: '' }); }}
                      className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full cursor-pointer hover:bg-red-600 transition-colors"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center bg-zinc-100 rounded-xl">
                    <i className="ri-image-add-line text-2xl text-zinc-400" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <span className="text-[12px] font-medium text-zinc-600 hover:text-zinc-900 transition-colors">
                    {heroPreview ? 'Alterar imagem' : 'Carregar imagem'}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'formacaoHeroImageUrl', setHeroPreview)} />
                </label>
                <p className="text-[11px] text-zinc-400 text-center">PNG ou JPG · Recomendado 220×800px</p>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">Visibilidade da Imagem</label>
                  <span className="text-[12px] font-bold text-zinc-700">{theme.formacaoHeroImageSize ?? 100}%</span>
                </div>
                <input
                  type="range" min={10} max={100} step={5}
                  value={theme.formacaoHeroImageSize ?? 100}
                  onChange={(e) => setTheme({ formacaoHeroImageSize: parseInt(e.target.value, 10) })}
                  className="w-full accent-emerald-600 cursor-pointer"
                  disabled={!heroPreview}
                />
                <div className="flex justify-between text-[10px] text-zinc-400 mt-0.5">
                  <span>Subtil</span><span>Total</span>
                </div>
                {!heroPreview && <p className="text-[10px] text-zinc-400 mt-1">Carregue uma imagem para ativar</p>}
              </div>
            </div>
          </div>

          {/* Favicon — partilhado com SIADAP */}
          <div className="border-t border-zinc-100 pt-5">
            <div className="flex items-start gap-5">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest">Favicon do Browser</p>
                  <span className="text-[10px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-medium">Partilhado com SIADAP</span>
                </div>
                <p className="text-[12px] text-zinc-400 mb-4">
                  Ícone que aparece no separador do browser. É partilhado entre ambos os módulos.
                </p>
                <div className="flex items-center gap-4">
                  <div className="border-2 border-dashed border-zinc-200 rounded-xl p-4 flex flex-col items-center gap-3 hover:border-zinc-400 transition-colors w-40 flex-shrink-0">
                    {faviconPreview ? (
                      <div className="relative">
                        <img src={faviconPreview} alt="Favicon" className="w-12 h-12 object-contain rounded-lg" />
                        <button
                          onClick={() => { setFaviconPreview(''); setTheme({ faviconUrl: '' }); }}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-red-500 text-white rounded-full cursor-pointer hover:bg-red-600 transition-colors"
                        >
                          <i className="ri-close-line text-[10px]" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-12 h-12 flex items-center justify-center bg-zinc-100 rounded-xl">
                        <i className="ri-global-line text-xl text-zinc-400" />
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <span className="text-[11px] font-medium text-zinc-600 hover:text-zinc-900 transition-colors">
                        {faviconPreview ? 'Alterar' : 'Carregar'}
                      </span>
                      <input
                        type="file"
                        accept="image/png,image/x-icon,image/svg+xml,image/jpeg"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, 'faviconUrl', setFaviconPreview)}
                      />
                    </label>
                  </div>

                  <div className="flex-1">
                    <p className="text-[11px] text-zinc-400 mb-2 font-medium">Pré-visualização no browser</p>
                    <div className="bg-zinc-100 rounded-t-lg px-3 py-2 flex items-center gap-2 w-fit max-w-[220px]">
                      {faviconPreview ? (
                        <img src={faviconPreview} alt="favicon" className="w-4 h-4 object-contain flex-shrink-0" />
                      ) : (
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-global-line text-zinc-400 text-xs" />
                        </div>
                      )}
                      <span className="text-[12px] text-zinc-600 truncate font-medium">
                        {theme.formacaoNome || 'Gabinete de Formação'}
                      </span>
                      <div className="w-3 h-3 flex items-center justify-center flex-shrink-0 ml-auto">
                        <i className="ri-close-line text-zinc-400 text-xs" />
                      </div>
                    </div>
                    <div className="bg-white border border-zinc-200 rounded-b-lg rounded-tr-lg h-8 w-full max-w-[280px]" />
                    <p className="text-[11px] text-zinc-400 mt-2">PNG, ICO ou SVG · Recomendado 32×32px</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Preview Mini ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
            <i className="ri-eye-line text-emerald-600 text-sm" />
          </div>
          <h2 className="text-[14px] font-semibold text-zinc-800">Pré-visualização da Sidebar</h2>
        </div>
        <div className="p-6">
          <div className="rounded-xl overflow-hidden border border-zinc-200 flex h-40">
            {/* Mini sidebar de Formação */}
            <div
              className="w-28 flex flex-col p-3 gap-2 flex-shrink-0"
              style={{
                backgroundColor: effectiveBg,
                backgroundImage: theme.formacaoHeroImageUrl ? `url(${theme.formacaoHeroImageUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {theme.formacaoLogoUrl ? (
                <img src={theme.formacaoLogoUrl} alt="Logo" className="h-5 object-contain mb-1" />
              ) : (
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: effectiveAccent, opacity: 0.8 }} />
                  <div className="h-2 rounded flex-1" style={{ backgroundColor: effectiveAccent, opacity: 0.3 }} />
                </div>
              )}
              {[0.7, 0.4, 0.4, 0.4].map((op, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: effectiveAccent, opacity: op }} />
                  <div className="h-1.5 rounded flex-1" style={{ backgroundColor: effectiveAccent, opacity: op * 0.5 }} />
                </div>
              ))}
            </div>
            {/* Mini content */}
            <div className="flex-1 p-4 flex flex-col gap-2 relative overflow-hidden" style={{ backgroundColor: theme.pageBg }}>
              <div className="h-3 rounded w-24" style={{ backgroundColor: theme.primaryColor, opacity: 0.15 }} />
              <div className="h-5 rounded w-40" style={{ backgroundColor: theme.primaryColor, opacity: 0.8 }} />
              <div className="flex gap-2 mt-1">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex-1 h-10 rounded-lg bg-white border border-zinc-100 p-2 flex flex-col gap-1">
                    <div className="h-1.5 rounded w-8 bg-zinc-200" />
                    <div className="h-2.5 rounded w-6" style={{ backgroundColor: theme.primaryColor, opacity: 0.7 }} />
                  </div>
                ))}
              </div>
              <div className="flex-1 rounded-lg bg-white border border-zinc-100" />
            </div>
          </div>
        </div>
      </div>

      {/* Save */}
      {saved && (
        <div className="flex items-center gap-2.5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-checkbox-circle-fill text-emerald-500 text-sm" />
          </div>
          <p className="text-[13px] text-emerald-700">Aparência da Gestão da Formação guardada com sucesso!</p>
        </div>
      )}

      <div className="flex justify-end pb-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white text-[13px] font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
        >
          {saving
            ? <><i className="ri-loader-4-line animate-spin" />A guardar...</>
            : <><i className="ri-save-line" />Guardar Aparência da Formação</>
          }
        </button>
      </div>
    </div>
  );
}